import type {
  ConciseBranch,
  ConciseChangeset,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryBranchesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryBranchesService'
import {
  assertNotEmpty,
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'

import { fetchConciseBranchKey } from '../keys/configuration'
import type { BranchKey } from './types'

export const useFetchConciseBranch = ({
  branchName,
  server,
  enabled,
  shouldIncludeBaseConciseChangesets,
}: {
  branchName: Optional<string>
  server: Optional<ConfigurableServer>
  enabled: boolean
  shouldIncludeBaseConciseChangesets?: boolean
}) => {
  const isEnabled = !!enabled && !!branchName && !!server
  const queryKey = isEnabled
    ? fetchConciseBranchKey({
        server,
        branchName,
        includeBaseChangesets: !!shouldIncludeBaseConciseChangesets,
      })
    : []

  // We intentionally use useQuery in order to avoid suspense from kicking in inside the branches panel,
  // making it snappier for users to click through branches in the branches tree.
  return useQuery({
    enabled: isEnabled,
    queryKey,
    queryFn: (): Promise<Optional<ConciseBranch>> => {
      if (!isEnabled) {
        return Promise.resolve(null)
      }
      assertNotEmpty(server)
      assertNotEmpty(branchName)

      return ConfigurationRepositoryBranchesService.branchServiceGetGetConciseBranch(
        {
          componentName: server.toLocaleLowerCase(),
          branchName,
          includeCurrentChangesets: true,
          includeBaseChangesets: shouldIncludeBaseConciseChangesets,
        },
      )
    },
    select: (
      conciseBranch,
    ): (ConciseChangeset & {
      branchKey: BranchKey
    })[] => {
      const catchUpChangesetKey = conciseBranch?.catchUpChangesetKey
      const catchupChangesetIndex =
        catchUpChangesetKey && conciseBranch?.currentChangesets
          ? conciseBranch?.currentChangesets
              .filter(notEmpty)
              .findIndex(
                (changeset) => changeset?.changesetKey === catchUpChangesetKey,
              )
          : -1

      return isEnabled
        ? [
            ...(conciseBranch?.currentChangesets
              ?.filter(notEmpty)
              .map((conciseChangeset, index) => ({
                ...conciseChangeset,
                branchKey:
                  catchupChangesetIndex !== -1 && index <= catchupChangesetIndex
                    ? {
                        name: conciseBranch?.followsBranchName ?? '-',
                        server: server ?? '',
                      }
                    : { name: branchName, server },
              }))
              ?.toReversed() ?? []),
            ...(conciseBranch?.baseChangesets
              ?.filter(notEmpty)
              ?.toReversed()
              .map((conciseChangeset) => {
                assertNotEmpty(conciseBranch.baseBranchName)

                return {
                  ...conciseChangeset,
                  branchKey: {
                    name: conciseBranch.baseBranchName,
                    server: server ?? '',
                  },
                }
              }) ?? []),
          ]
        : []
    },
    staleTime: Infinity,
  })
}
